import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../config/region';
import { formatVnd } from '../../lib/currency';
import DashboardNavBar from './DashboardNavBar';

// ════════════════════════════════════════════════════════════════════════════
// ServicePriceListEditor — OWNER / MANAGER CRUD for a venue's SERVICE CATALOGUE
// (the laundry / service price list). Manages service_items (with unit, price,
// optional price range, icon, i18n names), their variants, and venue-wide /
// item-scoped modifiers.
//
// Reads the catalogue via the PUBLIC GET /api/venues/:slug/services (same shape
// the consumer uses), and writes through the auth'd owner/manager CRUD at
// /api/me/venues/:venue_id/service-items[/:item_id][/variants], …/service-modifiers.
//
// Role-gated server-side to manager+. We resolve venue id + myRole from the slug
// via GET /venue-dashboard/:slug. All labels bilingual EN + VI.
// ════════════════════════════════════════════════════════════════════════════

function getAuthToken() {
  return (
    localStorage.getItem('venueapp_token') ||
    localStorage.getItem('venue_app_token') ||
    localStorage.getItem('token') ||
    null
  );
}

// ─── bilingual UI labels ─────────────────────────────────────────────────────
const LABELS = {
  title:          { en: 'Service Price List',     vi: 'Bảng giá dịch vụ' },
  loading:        { en: 'Loading…',                vi: 'Đang tải…' },
  addItem:        { en: 'Add service',             vi: 'Thêm dịch vụ' },
  newItem:        { en: 'New service',             vi: 'Dịch vụ mới' },
  editItem:       { en: 'Edit service',            vi: 'Sửa dịch vụ' },
  empty:          { en: 'No services yet',         vi: 'Chưa có dịch vụ' },
  emptySub:       { en: 'Add your first service to build the price list.', vi: 'Thêm dịch vụ đầu tiên để tạo bảng giá.' },
  name:           { en: 'Name (English)',          vi: 'Tên (Tiếng Anh)' },
  nameVi:         { en: 'Name (Vietnamese)',       vi: 'Tên (Tiếng Việt)' },
  category:       { en: 'Category',                vi: 'Danh mục' },
  unit:           { en: 'Unit',                    vi: 'Đơn vị' },
  price:          { en: 'Price ₫',                 vi: 'Giá ₫' },
  priceMax:       { en: 'Max price ₫ (range)',     vi: 'Giá tối đa ₫ (khoảng)' },
  isRange:        { en: 'Price is a range',        vi: 'Giá theo khoảng' },
  icon:           { en: 'Icon (emoji)',            vi: 'Biểu tượng (emoji)' },
  sortOrder:      { en: 'Sort order',              vi: 'Thứ tự' },
  save:           { en: 'Save',                    vi: 'Lưu' },
  cancel:         { en: 'Cancel',                  vi: 'Huỷ' },
  delete:         { en: 'Delete',                  vi: 'Xoá' },
  confirmDelete:  { en: 'Delete this service?',    vi: 'Xoá dịch vụ này?' },
  edit:           { en: 'Edit',                    vi: 'Sửa' },
  variants:       { en: 'Variants',                vi: 'Biến thể' },
  addVariant:     { en: '+ Variant',               vi: '+ Biến thể' },
  variantLabel:   { en: 'Variant label',           vi: 'Nhãn biến thể' },
  modifiers:      { en: 'Modifiers (venue-wide)',  vi: 'Tuỳ chọn (toàn địa điểm)' },
  addModifier:    { en: '+ Modifier',              vi: '+ Tuỳ chọn' },
  modifierName:   { en: 'Modifier name',           vi: 'Tên tuỳ chọn' },
  priceDelta:     { en: 'Price delta ₫',           vi: 'Chênh lệch giá ₫' },
  kind:           { en: 'Kind',                    vi: 'Loại' },
  surcharge:      { en: 'Surcharge',               vi: 'Phụ thu' },
  option:         { en: 'Option',                  vi: 'Tuỳ chọn' },
  needName:       { en: 'Name is required.',       vi: 'Cần nhập tên.' },
  accessDenied:   { en: 'Manager access required.', vi: 'Cần quyền quản lý.' },
  pleaseLogIn:    { en: 'Please log in.',          vi: 'Vui lòng đăng nhập.' },
  range:          { en: 'range',                   vi: 'khoảng' },
};

// unit → bilingual label
const UNITS = [
  { v: 'per_kg',      en: 'per kg',       vi: '/ kg' },
  { v: 'per_item',    en: 'per item',     vi: '/ món' },
  { v: 'per_pair',    en: 'per pair',     vi: '/ đôi' },
  { v: 'per_hour',    en: 'per hour',     vi: '/ giờ' },
  { v: 'per_session', en: 'per session',  vi: '/ buổi' },
  { v: 'flat',        en: 'flat',         vi: 'cố định' },
];

const MODIFIER_KINDS = ['surcharge', 'option'];

export default function ServicePriceListEditor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  const L = useCallback((k) => (LABELS[k] ? (LABELS[k][lang] || LABELS[k].en) : k), [lang]);
  const unitLabel = useCallback((v) => {
    const u = UNITS.find((x) => x.v === v);
    return u ? (u[lang] || u.en) : v;
  }, [lang]);

  const base = getApiBaseUrl();
  const token = getAuthToken();
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const [phase, setPhase] = useState('loading'); // loading | ready | error
  const [errType, setErrType] = useState(null);
  const [venue, setVenue] = useState(null);
  const [categories, setCategories] = useState([]); // [{name, items:[...]}]
  const [modifiers, setModifiers] = useState([]);   // venue-wide modifiers (item-scoped folded into items)
  const [editing, setEditing] = useState(null);     // item being edited, or {} for new

  // ── resolve venue + role ────────────────────────────────────────────────────
  const loadVenue = useCallback(async () => {
    if (!token) { setPhase('error'); setErrType(401); return null; }
    try {
      const res = await fetch(`${base}/venue-dashboard/${encodeURIComponent(slug)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { setPhase('error'); setErrType(401); return null; }
      const data = await res.json();
      if (!data.success || !data.venue) { setPhase('error'); setErrType('generic'); return null; }
      const role = data.myRole;
      if (role !== 'owner' && role !== 'manager') { setPhase('error'); setErrType(403); return null; }
      setVenue(data.venue);
      return data.venue;
    } catch (e) {
      console.error('[ServicePriceListEditor] venue', e);
      setPhase('error'); setErrType('generic');
      return null;
    }
  }, [base, slug, token]);

  // ── load catalogue (public, by slug) ────────────────────────────────────────
  const loadCatalog = useCallback(async () => {
    const res = await fetch(`${base}/venues/${encodeURIComponent(slug)}/services`);
    const data = await res.json();
    if (data.success && data.data) {
      setCategories(data.data.categories || []);
      // Extract venue-wide modifiers (they appear on every item identically;
      // grab the union, dedup by id, keeping those with no specific item match).
      const seen = new Map();
      for (const cat of data.data.categories || []) {
        for (const it of cat.items || []) {
          for (const m of it.modifiers || []) if (!seen.has(m.id)) seen.set(m.id, m);
        }
      }
      setModifiers(Array.from(seen.values()));
    }
  }, [base, slug]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const v = await loadVenue();
      if (!v || cancelled) return;
      await loadCatalog();
      if (!cancelled) setPhase('ready');
    })();
    return () => { cancelled = true; };
  }, [loadVenue, loadCatalog]);

  // ── item save (create or update) ────────────────────────────────────────────
  const saveItem = useCallback(async (form) => {
    const isNew = !form.id;
    const url = isNew
      ? `${base}/me/venues/${venue.id}/service-items`
      : `${base}/me/venues/${venue.id}/service-items/${form.id}`;
    const payload = {
      name: form.name,
      name_i18n: { en: form.name, vi: form.name_vi || '' },
      category: form.category || null,
      unit: form.unit,
      price_vnd: form.price_vnd != null && form.price_vnd !== '' ? Number(form.price_vnd) : null,
      price_max_vnd: form.is_range && form.price_max_vnd ? Number(form.price_max_vnd) : null,
      is_range: !!form.is_range,
      icon: form.icon || null,
      sort_order: form.sort_order != null && form.sort_order !== '' ? Number(form.sort_order) : 0,
    };
    const res = await fetch(url, { method: isNew ? 'POST' : 'PATCH', headers: authHeaders, body: JSON.stringify(payload) });
    const data = await res.json();
    if (data.success) { setEditing(null); await loadCatalog(); }
    else alert(data.message || 'Failed to save');
  }, [base, venue, authHeaders, loadCatalog]);

  // ── item delete (soft) ──────────────────────────────────────────────────────
  const deleteItem = useCallback(async (item) => {
    if (!window.confirm(L('confirmDelete'))) return;
    const res = await fetch(`${base}/me/venues/${venue.id}/service-items/${item.id}`, {
      method: 'DELETE', headers: authHeaders,
    });
    const data = await res.json();
    if (data.success) await loadCatalog();
    else alert(data.message || 'Failed to delete');
  }, [base, venue, authHeaders, loadCatalog, L]);

  // ── variant CRUD ────────────────────────────────────────────────────────────
  const addVariant = useCallback(async (item, label, priceVnd) => {
    const res = await fetch(`${base}/me/venues/${venue.id}/service-items/${item.id}/variants`, {
      method: 'POST', headers: authHeaders,
      body: JSON.stringify({ label, price_vnd: priceVnd != null && priceVnd !== '' ? Number(priceVnd) : null }),
    });
    const data = await res.json();
    if (data.success) await loadCatalog(); else alert(data.message || 'Failed');
  }, [base, venue, authHeaders, loadCatalog]);

  const deleteVariant = useCallback(async (item, variant) => {
    const res = await fetch(`${base}/me/venues/${venue.id}/service-items/${item.id}/variants/${variant.id}`, {
      method: 'DELETE', headers: authHeaders,
    });
    const data = await res.json();
    if (data.success) await loadCatalog(); else alert(data.message || 'Failed');
  }, [base, venue, authHeaders, loadCatalog]);

  // ── modifier CRUD (venue-wide) ──────────────────────────────────────────────
  const addModifier = useCallback(async (name, priceDelta, kind) => {
    const res = await fetch(`${base}/me/venues/${venue.id}/service-modifiers`, {
      method: 'POST', headers: authHeaders,
      body: JSON.stringify({ name, price_delta_vnd: priceDelta != null && priceDelta !== '' ? Number(priceDelta) : 0, kind }),
    });
    const data = await res.json();
    if (data.success) await loadCatalog(); else alert(data.message || 'Failed');
  }, [base, venue, authHeaders, loadCatalog]);

  const deleteModifier = useCallback(async (mod) => {
    const res = await fetch(`${base}/me/venues/${venue.id}/service-modifiers/${mod.id}`, {
      method: 'DELETE', headers: authHeaders,
    });
    const data = await res.json();
    if (data.success) await loadCatalog(); else alert(data.message || 'Failed');
  }, [base, venue, authHeaders, loadCatalog]);

  // ════════════════════════════════════════════════════════════════════════
  if (phase === 'loading') {
    return (
      <div style={S.screen}>
        <Header title={L('title')} onBack={() => navigate(`/venue-dashboard/${slug}`)} />
        <div style={S.centerBox}><Spinner /><p style={{ color: '#6b7280', fontSize: 14 }}>{L('loading')}</p></div>
        <DashboardNavBar />
      </div>
    );
  }
  if (phase === 'error') {
    const msg = errType === 401 ? L('pleaseLogIn') : errType === 403 ? L('accessDenied') : 'Something went wrong.';
    return (
      <div style={S.screen}>
        <Header title={L('title')} onBack={() => navigate(`/venue-dashboard/${slug}`)} />
        <div style={S.centerBox}>
          <div style={{ fontSize: 44 }}>{errType === 403 ? '🚫' : errType === 401 ? '🔒' : '⚠️'}</div>
          <p style={{ color: '#374151', fontSize: 15, textAlign: 'center', maxWidth: 280 }}>{msg}</p>
        </div>
        <DashboardNavBar />
      </div>
    );
  }

  const totalItems = categories.reduce((s, c) => s + (c.items?.length || 0), 0);

  return (
    <div style={S.screen}>
      <Header
        title={venue?.name || L('title')}
        onBack={() => navigate(`/venue-dashboard/${slug}`)}
        right={<button style={S.headerBtn} onClick={() => setEditing({})}>+ {L('addItem')}</button>}
      />

      <div style={S.body}>
        {totalItems === 0 ? (
          <div style={S.emptyBox}>
            <div style={{ fontSize: 48 }}>🧾</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#111827', marginTop: 8 }}>{L('empty')}</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{L('emptySub')}</div>
            <button style={{ ...S.primaryBtn, maxWidth: 220, margin: '16px auto 0' }} onClick={() => setEditing({})}>+ {L('addItem')}</button>
          </div>
        ) : (
          categories.map((cat) => (
            <div key={cat.name} style={{ marginBottom: 18 }}>
              <div style={S.catHeader}>{cat.name}</div>
              {(cat.items || []).map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  lang={lang}
                  L={L}
                  unitLabel={unitLabel}
                  onEdit={() => setEditing(itemToForm(item))}
                  onDelete={() => deleteItem(item)}
                  onAddVariant={(label, price) => addVariant(item, label, price)}
                  onDeleteVariant={(v) => deleteVariant(item, v)}
                />
              ))}
            </div>
          ))
        )}

        {/* venue-wide modifiers */}
        <ModifierSection
          modifiers={modifiers}
          lang={lang}
          L={L}
          onAdd={addModifier}
          onDelete={deleteModifier}
        />

        <div style={{ height: 48 }} />
      </div>

      {editing && (
        <ItemModal
          form={editing}
          lang={lang}
          L={L}
          unitLabel={unitLabel}
          onClose={() => setEditing(null)}
          onSave={saveItem}
        />
      )}

      <DashboardNavBar />
    </div>
  );
}

// ─── map an API item → editable form ─────────────────────────────────────────
function itemToForm(item) {
  return {
    id: item.id,
    name: item.name_en || '',
    name_vi: (item.name_i18n && item.name_i18n.vi) || '',
    category: item.category || '',
    unit: item.unit || 'per_item',
    price_vnd: item.price_vnd != null ? item.price_vnd : '',
    price_max_vnd: item.price_max_vnd != null ? item.price_max_vnd : '',
    is_range: !!item.is_range,
    icon: item.icon || '',
    sort_order: item.sort_order ?? 0,
  };
}

// ════════════════════════════════════════════════════════════════════════════
// Item row (display + inline variants)
// ════════════════════════════════════════════════════════════════════════════
function ItemRow({ item, lang, L, unitLabel, onEdit, onDelete, onAddVariant, onDeleteVariant }) {
  const [showVariants, setShowVariants] = useState(false);
  const [vLabel, setVLabel] = useState('');
  const [vPrice, setVPrice] = useState('');
  const name = (item.name_i18n && item.name_i18n[lang]) || item.name_en;

  const priceText = item.is_range && item.price_max_vnd != null
    ? `${formatVnd(Number(item.price_vnd || 0))}–${formatVnd(Number(item.price_max_vnd))}`
    : item.price_vnd != null ? formatVnd(Number(item.price_vnd)) : '—';

  return (
    <div style={S.itemCard}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 22 }}>{item.icon || '🧺'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>{name}</div>
          <div style={{ fontSize: 12, color: '#9ca3af' }}>{unitLabel(item.unit)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#9a3412' }}>{priceText}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <button style={S.smallBtn} onClick={onEdit}>✏️ {L('edit')}</button>
        <button style={{ ...S.smallBtn, color: '#dc2626', borderColor: '#fecaca' }} onClick={onDelete}>🗑 {L('delete')}</button>
        <button style={S.smallBtn} onClick={() => setShowVariants((v) => !v)}>
          {L('variants')} ({(item.variants || []).length})
        </button>
      </div>

      {showVariants && (
        <div style={{ marginTop: 8, borderTop: '1px solid #f3f4f6', paddingTop: 8 }}>
          {(item.variants || []).map((v) => (
            <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, padding: '3px 0' }}>
              <span>{(v.label_i18n && v.label_i18n[lang]) || v.label} · {v.price_vnd != null ? formatVnd(Number(v.price_vnd)) : '—'}</span>
              <button style={S.tinyDel} onClick={() => onDeleteVariant(v)}>✕</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <input style={{ ...S.input, marginBottom: 0, flex: 2 }} placeholder={L('variantLabel')} value={vLabel} onChange={(e) => setVLabel(e.target.value)} />
            <input style={{ ...S.input, marginBottom: 0, flex: 1 }} type="number" placeholder={L('price')} value={vPrice} onChange={(e) => setVPrice(e.target.value)} />
            <button
              style={S.smallBtn}
              onClick={() => { if (vLabel.trim()) { onAddVariant(vLabel.trim(), vPrice); setVLabel(''); setVPrice(''); } }}
            >
              {L('addVariant')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Modifier section (venue-wide)
// ════════════════════════════════════════════════════════════════════════════
function ModifierSection({ modifiers, lang, L, onAdd, onDelete }) {
  const [name, setName] = useState('');
  const [delta, setDelta] = useState('');
  const [kind, setKind] = useState('surcharge');
  const kindLabel = (k) => (k === 'surcharge' ? L('surcharge') : L('option'));

  return (
    <div style={{ marginTop: 8 }}>
      <div style={S.catHeader}>{L('modifiers')}</div>
      {modifiers.map((m) => (
        <div key={m.id} style={{ ...S.itemCard, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{(m.name_i18n && m.name_i18n[lang]) || m.name}</span>
            <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 8 }}>
              {kindLabel(m.kind)} · {m.price_delta_vnd >= 0 ? '+' : ''}{formatVnd(Number(m.price_delta_vnd || 0))}
            </span>
          </div>
          <button style={S.tinyDel} onClick={() => onDelete(m)}>✕</button>
        </div>
      ))}
      <div style={{ ...S.itemCard, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <input style={{ ...S.input, marginBottom: 0, flex: 2, minWidth: 120 }} placeholder={L('modifierName')} value={name} onChange={(e) => setName(e.target.value)} />
        <input style={{ ...S.input, marginBottom: 0, width: 110 }} type="number" placeholder={L('priceDelta')} value={delta} onChange={(e) => setDelta(e.target.value)} />
        <select style={{ ...S.input, marginBottom: 0, width: 120 }} value={kind} onChange={(e) => setKind(e.target.value)}>
          {MODIFIER_KINDS.map((k) => <option key={k} value={k}>{kindLabel(k)}</option>)}
        </select>
        <button style={S.smallBtn} onClick={() => { if (name.trim()) { onAdd(name.trim(), delta, kind); setName(''); setDelta(''); } }}>
          {L('addModifier')}
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Item create/edit modal
// ════════════════════════════════════════════════════════════════════════════
function ItemModal({ form: initial, lang, L, unitLabel, onClose, onSave }) {
  const [form, setForm] = useState({
    id: initial.id,
    name: initial.name || '',
    name_vi: initial.name_vi || '',
    category: initial.category || '',
    unit: initial.unit || 'per_item',
    price_vnd: initial.price_vnd ?? '',
    price_max_vnd: initial.price_max_vnd ?? '',
    is_range: !!initial.is_range,
    icon: initial.icon || '',
    sort_order: initial.sort_order ?? 0,
  });
  const [err, setErr] = useState('');
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = () => {
    if (!form.name.trim()) { setErr(L('needName')); return; }
    setErr('');
    onSave(form);
  };

  return (
    <Modal title={form.id ? L('editItem') : L('newItem')} onClose={onClose}>
      <label style={S.fieldLabel}>{L('name')}</label>
      <input style={S.input} value={form.name} onChange={(e) => set({ name: e.target.value })} />

      <label style={S.fieldLabel}>{L('nameVi')}</label>
      <input style={S.input} value={form.name_vi} onChange={(e) => set({ name_vi: e.target.value })} placeholder="Tiếng Việt" />

      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={S.fieldLabel}>{L('category')}</label>
          <input style={S.input} value={form.category} onChange={(e) => set({ category: e.target.value })} />
        </div>
        <div style={{ width: 90 }}>
          <label style={S.fieldLabel}>{L('icon')}</label>
          <input style={S.input} value={form.icon} onChange={(e) => set({ icon: e.target.value })} placeholder="🧺" />
        </div>
      </div>

      <label style={S.fieldLabel}>{L('unit')}</label>
      <select style={S.input} value={form.unit} onChange={(e) => set({ unit: e.target.value })}>
        {UNITS.map((u) => <option key={u.v} value={u.v}>{unitLabel(u.v)}</option>)}
      </select>

      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={S.fieldLabel}>{L('price')}</label>
          <input style={S.input} type="number" value={form.price_vnd} onChange={(e) => set({ price_vnd: e.target.value })} />
        </div>
        {form.is_range && (
          <div style={{ flex: 1 }}>
            <label style={S.fieldLabel}>{L('priceMax')}</label>
            <input style={S.input} type="number" value={form.price_max_vnd} onChange={(e) => set({ price_max_vnd: e.target.value })} />
          </div>
        )}
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 8px', fontSize: 14, color: '#374151' }}>
        <input type="checkbox" checked={form.is_range} onChange={(e) => set({ is_range: e.target.checked })} />
        {L('isRange')}
      </label>

      <label style={S.fieldLabel}>{L('sortOrder')}</label>
      <input style={S.input} type="number" value={form.sort_order} onChange={(e) => set({ sort_order: e.target.value })} />

      {err && <div style={S.errorBox}>{err}</div>}
      <div style={S.modalActions}>
        <button style={S.secondaryBtn} onClick={onClose}>{L('cancel')}</button>
        <button style={S.primaryBtn} onClick={submit}>{L('save')}</button>
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// shared UI
// ════════════════════════════════════════════════════════════════════════════
function Header({ title, onBack, right }) {
  return (
    <div style={S.header}>
      <button style={S.backBtn} onClick={onBack}>←</button>
      <span style={S.headerTitle} title={title}>{title}</span>
      <div style={{ minWidth: 40, display: 'flex', justifyContent: 'flex-end' }}>{right || <div style={{ width: 40 }} />}</div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalSheet} onClick={(e) => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <span style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>{title}</span>
          <button style={S.modalClose} onClick={onClose}>✕</button>
        </div>
        <div style={{ overflowY: 'auto', padding: 16 }}>{children}</div>
      </div>
    </div>
  );
}

function Spinner({ size = 40, color = '#f97316' }) {
  return (
    <>
      <div style={{ width: size, height: size, border: '4px solid rgba(0,0,0,0.08)', borderTop: `4px solid ${color}`, borderRadius: '50%', animation: 'hl-spin 0.9s linear infinite', margin: '0 auto' }} />
      <style>{`@keyframes hl-spin{0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// styles
// ════════════════════════════════════════════════════════════════════════════
const S = {
  screen: { minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f9fafb', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  header: { height: 64, background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #fbbf24 100%)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px', flexShrink: 0, position: 'sticky', top: 0, zIndex: 100 },
  backBtn: { background: 'transparent', border: 'none', color: 'white', fontSize: 22, fontWeight: 700, cursor: 'pointer', width: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  headerTitle: { color: 'white', fontWeight: 700, fontSize: 16, flex: 1, textAlign: 'center', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', padding: '0 8px' },
  headerBtn: { background: 'rgba(255,255,255,0.22)', border: '1px solid rgba(255,255,255,0.5)', color: '#fff', borderRadius: 8, padding: '6px 10px', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' },
  body: { flex: 1, padding: '14px 14px 0', overflowY: 'auto' },
  centerBox: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 24 },
  emptyBox: { textAlign: 'center', padding: '48px 16px' },
  catHeader: { fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '6px 2px 8px' },
  itemCard: { background: '#fff', borderRadius: 12, padding: 12, marginBottom: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  smallBtn: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer' },
  tinyDel: { background: '#fee2e2', border: 'none', borderRadius: 6, width: 26, height: 26, color: '#991b1b', cursor: 'pointer', fontSize: 12 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  modalSheet: { background: '#fff', width: '100%', maxWidth: 560, maxHeight: '92vh', borderTopLeftRadius: 18, borderTopRightRadius: 18, display: 'flex', flexDirection: 'column' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #f3f4f6', position: 'sticky', top: 0, background: '#fff', borderTopLeftRadius: 18, borderTopRightRadius: 18 },
  modalClose: { background: '#f3f4f6', border: 'none', borderRadius: 8, width: 32, height: 32, fontSize: 15, cursor: 'pointer' },
  modalActions: { display: 'flex', gap: 10, marginTop: 16 },
  fieldLabel: { fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4, marginTop: 8, display: 'block' },
  input: { width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 12px', fontSize: 14, marginBottom: 4, fontFamily: 'inherit' },
  errorBox: { marginTop: 12, padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 13 },
  primaryBtn: { flex: 1, background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 100%)', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 18px', fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  secondaryBtn: { flex: 1, background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 12, padding: '12px 18px', fontSize: 15, fontWeight: 700, cursor: 'pointer' },
};
